import { distToSegment, type Pt } from './boardMath';

/*
 * Распознавание фигур в росчерке (решение Ильи 16.09.2026): нарисовал от руки
 * круг — получил ровный круг, квадрат — ровный квадрат.
 *
 * Считается по геометрии самого росчерка, без обучения и без библиотек: у
 * доски в звонке нет ни места под модель, ни права тратить на неё время между
 * двумя штрихами. Признаков хватает трёх: замкнут ли контур, сколько в нём
 * резких поворотов и насколько он круглый по отношению площади к периметру.
 *
 * Порядок разбора — от частного к общему. Сначала прямая: у неё все точки
 * лежат на одной линии, и ни один другой признак этого не даёт. Потом
 * замкнутые: по числу углов — треугольник, четырёхугольник, а если углов не
 * нашлось и контур круглый — круг. Не подошло ничего — росчерк остаётся собой.
 */

/**
 * Что получилось из росчерка.
 *
 * Фигуры правильные (решение Ильи 16.09.2026): круг, а не овал; квадрат, а не
 * прямоугольник; равносторонний треугольник. От руки никто не рисует
 * разносторонний треугольник нарочно — он получается сам, и выравнивать его
 * надо до правильного, иначе выравнивание half-hearted.
 */
export type Shape =
  | { kind: 'line'; a: Pt; b: Pt }
  | { kind: 'square'; cx: number; cy: number; side: number }
  /** Равносторонний, вершиной вверх: `angle` — направление на первую вершину. */
  | { kind: 'triangle'; cx: number; cy: number; r: number; angle: number }
  | { kind: 'circle'; cx: number; cy: number; r: number };

export type ShapeConfig = {
  /** Насколько точки могут отходить от прямой, в долях её длины. */
  lineTolerance: number;
  /** Разрыв между началом и концом, при котором контур считается замкнутым (в долях размера). */
  closeGap: number;
  /** Круче этого поворот считается углом, в радианах. */
  cornerAngle: number;
  /**
   * Ниже этой «круглости» (4π·площадь / периметр²) замкнутый контур без углов
   * кругом уже не считается. Порог низкий намеренно: круг от руки выходит
   * мятым, а квадраты и треугольники отбираются раньше — по числу углов.
   */
  circleness: number;
  /** Короче этого росчерк не разбираем: случайный тычок фигурой не станет. */
  minPoints: number;
  /** Столько точек нужно замкнутой фигуре, чтобы верить её углам. */
  minClosedPoints: number;
};

export const SHAPE_DEFAULTS: ShapeConfig = {
  lineTolerance: 0.12,
  closeGap: 0.25,
  cornerAngle: 1.15,
  circleness: 0.62,
  minPoints: 8,
  minClosedPoints: 16,
};

const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);

/** Габариты росчерка: по ним меряются все допуски, чтобы они не зависели от размера. */
export function bounds(pts: Pt[]): { minX: number; minY: number; maxX: number; maxY: number; size: number } {
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return { minX, minY, maxX, maxY, size: Math.max(maxX - minX, maxY - minY) };
}

const perimeter = (pts: Pt[]) => pts.reduce((s, p, i) => (i ? s + dist(pts[i - 1], p) : 0), 0);

/** Площадь многоугольника по формуле шнурования; знак не важен, берём модуль. */
export function polygonArea(pts: Pt[]): number {
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

/**
 * Разрежаем росчерк до опорных точек: подряд идущие близкие точки на углы не
 * влияют, а шума добавляют. Шаг — доля размера фигуры.
 */
function simplify(pts: Pt[], step: number): Pt[] {
  const out: Pt[] = [pts[0]];
  for (const p of pts) if (dist(out[out.length - 1], p) >= step) out.push(p);
  const last = pts[pts.length - 1];
  if (dist(out[out.length - 1], last) > 0) out.push(last);
  return out;
}

/**
 * Углы росчерка: точки, где направление меняется круче порога.
 *
 * Направление берётся не по соседям, а на плече в несколько точек. Рука
 * дрожит, и на соседних точках поворот размазан: угол квадрата, нарисованного
 * от руки, по трём подряд точкам не виден вовсе. Подряд идущие «углы»
 * схлопываются в один — иначе один поворот считался бы двумя.
 */
export function corners(pts: Pt[], cfg: ShapeConfig, closed = false): Pt[] {
  const n = pts.length;
  const arm = Math.max(2, Math.round(n / 14));
  const found: { p: Pt; i: number }[] = [];
  const from = closed ? 0 : arm;
  const to = closed ? n : n - arm;
  for (let i = from; i < to; i++) {
    // У замкнутого контура угол бывает и на стыке начала с концом, поэтому
    // индексы идут по кольцу: иначе у квадрата находится три угла из четырёх.
    const a = pts[closed ? (i - arm + n) % n : i - arm];
    const b = pts[i];
    const c = pts[closed ? (i + arm) % n : i + arm];
    const ab = Math.atan2(b.y - a.y, b.x - a.x);
    const bc = Math.atan2(c.y - b.y, c.x - b.x);
    let turn = Math.abs(bc - ab);
    if (turn > Math.PI) turn = 2 * Math.PI - turn;
    if (turn < cfg.cornerAngle) continue;
    // Подряд идущие «углы» — один поворот, размазанный дрожанием руки.
    const prev = found[found.length - 1];
    if (prev && i - prev.i <= arm) continue;
    found.push({ p: b, i });
  }
  // На кольце первый и последний могут оказаться тем же углом.
  if (closed && found.length > 1 && found[0].i + n - found[found.length - 1].i <= arm) found.pop();
  return found.map((f) => f.p);
}

/** Наибольшее отклонение точек от прямой через первую и последнюю. */
function lineDeviation(pts: Pt[]): number {
  const a = pts[0];
  const b = pts[pts.length - 1];
  const len = dist(a, b);
  if (len === 0) return Infinity;
  let worst = 0;
  for (const p of pts) {
    const d = Math.abs((b.x - a.x) * (a.y - p.y) - (a.x - p.x) * (b.y - a.y)) / len;
    if (d > worst) worst = d;
  }
  return worst / len;
}

/**
 * Во что превратить росчерк. `null` — ни во что: пусть остаётся как нарисован.
 */
export function recognize(raw: Pt[], config: Partial<ShapeConfig> = {}): Shape | null {
  const cfg = { ...SHAPE_DEFAULTS, ...config };
  if (raw.length < cfg.minPoints) return null;
  const box = bounds(raw);
  if (box.size <= 0) return null;

  // Прямая: все точки почти на одной линии. Проверяется первой — замкнутость
  // и углы у прямой тоже «сходятся», но смысла в них нет.
  if (lineDeviation(raw) <= cfg.lineTolerance) {
    return { kind: 'line', a: raw[0], b: raw[raw.length - 1] };
  }

  const closed = dist(raw[0], raw[raw.length - 1]) <= cfg.closeGap * box.size;
  if (!closed) return null;

  /*
   * Замкнутую фигуру разбираем, только когда точек хватает. На быстром
   * росчерке в десяток точек углы находятся где попало: и круг, и рамка
   * уезжали в треугольник. Прямой это не мешает — она проверена выше и точек
   * требует меньше.
   */
  if (raw.length < cfg.minClosedPoints) return null;

  /*
   * Шаг разрежения — доля периметра, а не габарита. По габариту у вытянутой
   * рамки короткие стороны укладывались в один-два шага, и углы на них
   * сливались: находилось два угла из четырёх (замечание Ильи 16.09.2026).
   */
  const per0 = perimeter([...raw, raw[0]]);
  const pts = simplify(raw, per0 / 40);
  if (pts.length < 4) return null;
  const found = corners(pts, cfg, true);

  const per = per0;
  const area = polygonArea(raw);
  const roundness = per > 0 ? (4 * Math.PI * area) / (per * per) : 0;

  const cx = (box.minX + box.maxX) / 2;
  const cy = (box.minY + box.maxY) / 2;
  const half = (v: number) => v / 2;

  /*
   * Сначала углы, потом круглость. Раньше первым шёл круг, и квадрат с
   * дрожащими сторонами проходил по «круглости» вперёд разбора углов
   * (замечание Ильи 16.09.2026: часть кругов не узнавалась, а часть квадратов
   * становилась кругами).
   */
  if (found.length === 3) {
    /*
     * Равносторонний и всегда одной ориентации: вершина вверх, основание
     * параллельно горизонту (решение Ильи 16.09.2026). Наклон рисующей руки
     * не сохраняем — от руки треугольник выходит косым, и «выровняли, но
     * оставили косым» это не выравнивание.
     */
    const gx = (found[0].x + found[1].x + found[2].x) / 3;
    const gy = (found[0].y + found[1].y + found[2].y) / 3;
    const r = found.reduce((s2, p) => s2 + dist({ x: gx, y: gy }, p), 0) / 3;
    return { kind: 'triangle', cx: gx, cy: gy, r, angle: -Math.PI / 2 };
  }

  if (found.length === 4 || found.length === 5) {
    // Квадрат: сторона — среднее габаритов, центр прежний.
    const side = (box.maxX - box.minX + (box.maxY - box.minY)) / 2;
    return { kind: 'square', cx, cy, side };
  }

  /*
   * Круг: углов почти нет и контур достаточно круглый. Радиус — средний из
   * полуосей: овал от руки означает круг, нарисованный второпях.
   *
   * Двух ложных углов хватает, чтобы круг перестал быть кругом, поэтому порог
   * не «ни одного», а «не больше двух»: на мятом контуре пара резких мест
   * находится почти всегда (замечание Ильи 16.09.2026 — круг оставался
   * овалом, то есть не распознавался вовсе).
   */
  if (found.length <= 2 && roundness >= cfg.circleness) {
    const r = (half(box.maxX - box.minX) + half(box.maxY - box.minY)) / 2;
    return { kind: 'circle', cx, cy, r };
  }

  /*
   * Замкнутый контур, не подошедший ни под что: единственная догадка — квадрат,
   * и только когда точек хватает, чтобы признакам верить. У квадрата круглость
   * 0.785, у равностороннего треугольника 0.60; ниже 0.6 не гадаем вовсе.
   *
   * Треугольник сюда не попадает нарочно: он надёжно узнаётся по трём углам, а
   * на быстром росчерке с десятком точек догадка по круглости превращала в
   * треугольник и круги, и рамки.
   */
  if (raw.length >= 16 && roundness >= 0.6) {
    const side = (box.maxX - box.minX + (box.maxY - box.minY)) / 2;
    return { kind: 'square', cx, cy, side };
  }

  return null;
}

/** Фигура обратно в точки: доска хранит штрихи, а не фигуры. */
export function shapePoints(shape: Shape, steps = 48): Pt[] {
  if (shape.kind === 'line') return [shape.a, shape.b];
  if (shape.kind === 'square') {
    const h = shape.side / 2;
    const a = { x: shape.cx - h, y: shape.cy - h };
    const b = { x: shape.cx + h, y: shape.cy + h };
    return [
      { x: a.x, y: a.y },
      { x: b.x, y: a.y },
      { x: b.x, y: b.y },
      { x: a.x, y: b.y },
      { x: a.x, y: a.y },
    ];
  }
  if (shape.kind === 'triangle') {
    const pts = [0, 1, 2].map((i) => ({
      x: shape.cx + shape.r * Math.cos(shape.angle + (i * 2 * Math.PI) / 3),
      y: shape.cy + shape.r * Math.sin(shape.angle + (i * 2 * Math.PI) / 3),
    }));
    return [...pts, pts[0]];
  }
  const out: Pt[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 2 * Math.PI;
    out.push({ x: shape.cx + shape.r * Math.cos(t), y: shape.cy + shape.r * Math.sin(t) });
  }
  return out;
}

/*
 * Фигуру, в отличие от росчерка, можно взять и подвинуть (решение Ильи
 * 16.09.2026). Дальше — геометрия этого: за что фигура берётся, где у неё
 * ручки и во что она превращается, когда ручку тянут. Всё чистое, без DOM:
 * экран только считает точку касания и зовёт эти функции.
 */

/** Габариты фигуры: по ним ставятся ручки и рисуется рамка выделения. */
export function shapeBox(shape: Shape): { minX: number; minY: number; maxX: number; maxY: number } {
  if (shape.kind === 'circle') {
    return { minX: shape.cx - shape.r, minY: shape.cy - shape.r, maxX: shape.cx + shape.r, maxY: shape.cy + shape.r };
  }
  const b = bounds(shapePoints(shape));
  return { minX: b.minX, minY: b.minY, maxX: b.maxX, maxY: b.maxY };
}

/** Точка, вокруг которой фигура растёт: у прямой — середина, у остальных — их центр. */
export function shapeCenter(shape: Shape): Pt {
  if (shape.kind === 'line') return { x: (shape.a.x + shape.b.x) / 2, y: (shape.a.y + shape.b.y) / 2 };
  return { x: shape.cx, y: shape.cy };
}

/**
 * Ручки растяжения: у прямой — её концы (тянется тот, за который взялись), у
 * замкнутых фигур — углы габарита, и любой из них растит фигуру целиком.
 * Растянуть круг в овал нельзя нарочно: фигуры правильные, и ручка меняет
 * размер, а не форму.
 */
export function shapeHandles(shape: Shape): Pt[] {
  if (shape.kind === 'line') return [shape.a, shape.b];
  const b = shapeBox(shape);
  return [
    { x: b.minX, y: b.minY },
    { x: b.maxX, y: b.minY },
    { x: b.maxX, y: b.maxY },
    { x: b.minX, y: b.maxY },
  ];
}

/** Номер ручки под точкой касания или -1: ручки перекрывают саму фигуру. */
export function handleAt(shape: Shape, p: Pt, tol: number): number {
  const hs = shapeHandles(shape);
  let best = -1;
  let bestD = tol;
  hs.forEach((h, i) => {
    const d = Math.hypot(h.x - p.x, h.y - p.y);
    if (d <= bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

/** Точка в пределах прямой доступности, чтобы взять фигуру: рядом с линией или внутри неё. */
export function hitShape(shape: Shape, p: Pt, tol: number): boolean {
  if (shape.kind === 'line') return distToSegment(p, shape.a, shape.b) <= tol;
  if (shape.kind === 'circle') return Math.hypot(p.x - shape.cx, p.y - shape.cy) <= shape.r + tol;
  const pts = shapePoints(shape);
  for (let i = 1; i < pts.length; i++) if (distToSegment(p, pts[i - 1], pts[i]) <= tol) return true;
  // Внутри тоже считается: за фигуру берут, а не за её контур.
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i];
    const b = pts[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Переносит фигуру целиком. */
export function moveShape(shape: Shape, dx: number, dy: number): Shape {
  if (shape.kind === 'line') {
    return { kind: 'line', a: { x: shape.a.x + dx, y: shape.a.y + dy }, b: { x: shape.b.x + dx, y: shape.b.y + dy } };
  }
  return { ...shape, cx: shape.cx + dx, cy: shape.cy + dy };
}

/** Меньше этого фигуру не ужать: нулевой размер — это её исчезновение. */
export const MIN_SHAPE_SIZE = 0.02;

/**
 * Тянут ручку `handle` в точку `p`. Считается всегда от фигуры на начало
 * жеста, а не от предыдущего кадра: иначе округления накапливались бы и
 * фигура уезжала сама.
 *
 * У прямой двигается взятый конец, у остальных размер меняется целиком —
 * во сколько раз ручка отошла от центра, во столько же растёт фигура.
 */
export function resizeShape(shape: Shape, handle: number, p: Pt): Shape {
  if (shape.kind === 'line') {
    return handle === 0 ? { kind: 'line', a: p, b: shape.b } : { kind: 'line', a: shape.a, b: p };
  }
  const c = shapeCenter(shape);
  const hs = shapeHandles(shape);
  const was = Math.hypot(hs[handle].x - c.x, hs[handle].y - c.y);
  if (was <= 0) return shape;
  const k = Math.hypot(p.x - c.x, p.y - c.y) / was;
  if (shape.kind === 'square') return { ...shape, side: Math.max(MIN_SHAPE_SIZE, shape.side * k) };
  return { ...shape, r: Math.max(MIN_SHAPE_SIZE / 2, shape.r * k) };
}
